import { useEffect, useState, useCallback } from "react";
import { Loader2, Download, Search, Mail, MailCheck, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { formatApiError } from "../../lib/api";

const STATUS_META = {
  pending: { label: "Pending Verification", cls: "text-amber" },
  verified: { label: "Verified", cls: "text-emerald-400" },
  unsubscribed: { label: "Unsubscribed", cls: "text-firered" },
  active: { label: "Verified", cls: "text-emerald-400" },
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

export default function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState("");
  const [expiry, setExpiry] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/newsletter").then(({ data }) => setSubs(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); api.get("/admin/newsletter/settings").then(({ data }) => setExpiry(data.coupon_expiry || "")).catch(() => {}); }, [load]);

  const norm = (s) => (s === "active" ? "verified" : s);
  const exportCsv = async () => {
    const { data } = await api.get("/admin/newsletter/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = "vip_subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const resend = async (s, kind) => {
    setBusy(`${s.subscriber_id}-${kind}`);
    try {
      await api.post(`/admin/newsletter/${s.subscriber_id}/resend-${kind}`);
      toast.success(kind === "verification" ? "Verification email sent" : "Welcome email sent");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send email"); }
    finally { setBusy(""); }
  };
  const saveExpiry = async () => {
    try { await api.put("/admin/newsletter/settings", { coupon_expiry: expiry }); toast.success("Coupon expiry saved"); }
    catch { toast.error("Could not save"); }
  };

  const filtered = subs.filter((s) => {
    const matchQ = !q || s.email.toLowerCase().includes(q.toLowerCase()) || (s.name || "").toLowerCase().includes(q.toLowerCase());
    const matchS = statusFilter === "all" || norm(s.status) === statusFilter;
    return matchQ && matchS;
  });
  const verified = subs.filter((s) => norm(s.status) === "verified").length;
  const pending = subs.filter((s) => norm(s.status) === "pending").length;

  return (
    <div className="space-y-6" data-testid="admin-newsletter">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">VIP List Subscribers</h1>
          <p className="mt-2 text-muted-foreground">{verified} verified · {pending} pending verification. Double opt-in with auto 10% welcome coupon.</p>
        </div>
        <Button data-testid="export-csv-btn" onClick={exportCsv} className="cf-gold-btn font-semibold gap-2"><Download className="h-4 w-4" /> Export CSV</Button>
      </div>

      <GlassCard className="p-5" hover={false}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-white/70 text-xs">Welcome coupon expiry (default: end of season)</Label>
            <Input type="date" data-testid="coupon-expiry-input" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white w-48" />
          </div>
          <Button variant="outline" className="border-white/20 text-white gap-2" onClick={saveExpiry} data-testid="save-expiry-btn"><Save className="h-4 w-4" /> Save</Button>
        </div>
      </GlassCard>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="newsletter-search" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-black/30 border-white/15 text-white" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52 bg-black/30 border-white/15 text-white" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending Verification</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <GlassCard className="p-0 overflow-x-auto" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground flex flex-col items-center gap-3"><Mail className="h-8 w-8 text-white/30" /> No subscribers found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Email</TableHead>
                <TableHead className="text-white/70">Status</TableHead>
                <TableHead className="text-white/70">Source</TableHead>
                <TableHead className="text-white/70">Subscribed</TableHead>
                <TableHead className="text-white/70">Verified</TableHead>
                <TableHead className="text-white/70">Unsub.</TableHead>
                <TableHead className="text-white/70">Coupon</TableHead>
                <TableHead className="text-white/70">Redeemed</TableHead>
                <TableHead className="text-white/70 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const st = STATUS_META[s.status] || { label: s.status, cls: "text-white/60" };
                return (
                  <TableRow key={s.subscriber_id || s.email} className="border-white/10" data-testid={`subscriber-row-${s.email}`}>
                    <TableCell className="text-white font-medium">{s.email}{s.customer_id && <span className="ml-1.5 text-[10px] text-gold border border-gold/40 rounded px-1">ACCT</span>}</TableCell>
                    <TableCell className={st.cls} data-testid={`sub-status-${s.email}`}>{st.label}</TableCell>
                    <TableCell className="text-muted-foreground capitalize">{s.source || s.source_page || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(s.created_at)}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(s.verified_at)}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(s.unsubscribed_at)}</TableCell>
                    <TableCell className="text-gold font-mono text-xs">{s.voucher_code || "—"}</TableCell>
                    <TableCell className={s.coupon_redeemed ? "text-emerald-400" : "text-white/40"}>{s.voucher_code ? (s.coupon_redeemed ? "Yes" : "No") : "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {norm(s.status) !== "verified" && norm(s.status) !== "unsubscribed" && (
                        <button title="Resend verification" data-testid={`resend-verify-${s.email}`} disabled={busy === `${s.subscriber_id}-verification`} onClick={() => resend(s, "verification")} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10 disabled:opacity-40">{busy === `${s.subscriber_id}-verification` ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
                      )}
                      {norm(s.status) === "verified" && (
                        <button title="Resend welcome email" data-testid={`resend-welcome-${s.email}`} disabled={busy === `${s.subscriber_id}-welcome`} onClick={() => resend(s, "welcome")} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10 disabled:opacity-40">{busy === `${s.subscriber_id}-welcome` ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}</button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </GlassCard>
    </div>
  );
}
